/**
 * Jul-2026 (User-Wunsch) — Zahnrad: B2B Kauf/Vermittlung-Regeln des Verkäufers.
 * Pro Marke, Modell, Fahrzeugtyp und/oder Standzeit festlegen, ob fremde
 * Händler nur „Kauf", nur „Vermittlung" oder beides auswählen dürfen.
 * Ohne Regel = beides erlaubt. (Nov-2026: + Fahrzeugtyp & Standzeit)
 */
import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, Settings } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { MOTORCYCLE_VEHICLE_TYPES } from "../../lib/listingConstants";

const ALLOWED = [
    { key: "beide", label: "Kauf + Vermittlung" },
    { key: "kauf", label: "Nur Kauf" },
    { key: "vermittlung", label: "Nur Vermittlung" },
];

const EMPTY_DRAFT = { brand: "", model: "", vehicle_type: "", min_standtage: "", allowed: "beide" };

export default function B2BTradeRulesModal({ onClose }) {
    const [rules, setRules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [draft, setDraft] = useState(EMPTY_DRAFT);
    // Aug-2026 (User-Wunsch) — Regel-Vorschau: betroffene eigene Fahrzeuge
    const [preview, setPreview] = useState(null);
    useEffect(() => {
        const hasCriteria = draft.brand.trim() || draft.model.trim() || draft.vehicle_type || String(draft.min_standtage).trim();
        if (!hasCriteria) { setPreview(null); return; }
        const t = setTimeout(() => {
            api.get("/dealer/b2b/trade-rules/preview", { params: {
                brand: draft.brand.trim(), model: draft.model.trim(),
                vehicle_type: draft.vehicle_type,
                min_standtage: String(draft.min_standtage).trim() ? parseInt(draft.min_standtage, 10) : undefined,
            } }).then((r) => setPreview(r.data)).catch(() => setPreview(null));
        }, 450);
        return () => clearTimeout(t);
    }, [draft]);

    useEffect(() => {
        api.get("/dealer/b2b/trade-rules")
            .then((r) => setRules(r.data?.rules || []))
            .catch(() => toast.error("Regeln konnten nicht geladen werden"))
            .finally(() => setLoading(false));
    }, []);

    const save = async (next) => {
        setBusy(true);
        try {
            const r = await api.put("/dealer/b2b/trade-rules", { rules: next });
            setRules(r.data?.rules || next);
            toast.success("Regeln gespeichert");
        } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
        finally { setBusy(false); }
    };

    const addRule = () => {
        if (!draft.brand.trim() && !draft.vehicle_type && !String(draft.min_standtage).trim()) {
            toast.error("Bitte mindestens ein Kriterium angeben (Marke, Fahrzeugtyp oder Standzeit)");
            return;
        }
        const next = [...rules, {
            brand: draft.brand.trim() || null,
            model: draft.model.trim() || null,
            vehicle_type: draft.vehicle_type || null,
            min_standtage: String(draft.min_standtage).trim() ? parseInt(draft.min_standtage, 10) : null,
            allowed: draft.allowed,
        }];
        setDraft(EMPTY_DRAFT);
        save(next);
    };

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-lg" data-testid="b2b-trade-rules-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><Settings className="w-5 h-5 text-[#FFCC00]" /> Kauf / Vermittlung — Regeln</DialogTitle>
                    <DialogDescription>
                        Legen Sie je Marke, Modell, Fahrzeugtyp und/oder Standzeit fest, was fremde Händler bei Ihren Fahrzeugen auswählen dürfen.
                        Ohne Regel gilt: <b>beides erlaubt</b>.
                    </DialogDescription>
                </DialogHeader>
                {loading ? (
                    <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
                ) : (
                    <div className="space-y-3">
                        {rules.length > 0 ? (
                            <div className="space-y-1.5">
                                {rules.map((r, i) => (
                                    <div key={i} className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-sm px-2.5 py-1.5 text-sm flex-wrap" data-testid={`b2b-trade-rule-${i}`}>
                                        <span className="font-bold">{r.brand || "Alle Marken"}</span>
                                        {r.model && <span className="text-gray-500">· {r.model}</span>}
                                        {r.vehicle_type && <span className="text-gray-500">· {r.vehicle_type}</span>}
                                        {(r.min_standtage || r.min_standtage === 0) && <span className="text-gray-500">· Standzeit ab {r.min_standtage} T</span>}
                                        <span className={`ml-auto text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${r.allowed === "beide" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                                            {ALLOWED.find((a) => a.key === r.allowed)?.label || r.allowed}
                                        </span>
                                        <button type="button" disabled={busy} onClick={() => save(rules.filter((_, j) => j !== i))}
                                            className="text-red-500 hover:text-red-700" title="Regel löschen" data-testid={`b2b-trade-rule-delete-${i}`}>
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-[12px] text-gray-400 border border-dashed border-gray-200 rounded-sm px-3 py-2">Noch keine Regeln — für alle Fahrzeuge sind Kauf UND Vermittlung erlaubt.</p>
                        )}
                        <div className="grid grid-cols-2 gap-2 bg-white border-2 border-[#111827] rounded-sm p-3" data-testid="b2b-trade-rule-form">
                            <Input value={draft.brand} onChange={(e) => setDraft({ ...draft, brand: e.target.value })} placeholder="Marke (z. B. Piaggio)" className="h-9" data-testid="b2b-trade-rule-brand" />
                            <Input value={draft.model} onChange={(e) => setDraft({ ...draft, model: e.target.value })} placeholder="Modell (optional)" className="h-9" data-testid="b2b-trade-rule-model" />
                            <select value={draft.vehicle_type} onChange={(e) => setDraft({ ...draft, vehicle_type: e.target.value })}
                                className="h-9 text-sm border-2 border-gray-200 rounded-sm px-2" data-testid="b2b-trade-rule-vehicle-type">
                                <option value="">Fahrzeugtyp: alle</option>
                                {MOTORCYCLE_VEHICLE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                            <Input type="number" min="0" value={draft.min_standtage}
                                onChange={(e) => setDraft({ ...draft, min_standtage: e.target.value })}
                                placeholder="Standzeit ab (Tage, optional)" className="h-9" data-testid="b2b-trade-rule-standtage" />
                            <select value={draft.allowed} onChange={(e) => setDraft({ ...draft, allowed: e.target.value })}
                                className="h-9 text-sm border-2 border-gray-200 rounded-sm px-2 font-bold" data-testid="b2b-trade-rule-allowed">
                                {ALLOWED.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
                            </select>
                            <Button type="button" onClick={addRule} disabled={busy} className="h-9 bg-[#FFCC00] hover:bg-[#e6b800] text-[#111827] font-bold" data-testid="b2b-trade-rule-add">
                                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Regel hinzufügen</>}
                            </Button>
                            <p className="col-span-2 text-[10px] text-gray-500">Mindestens ein Kriterium angeben — z. B. nur „Standzeit ab 90 Tage" → gilt für alle Fahrzeuge ab 90 Tagen Standzeit. Die spezifischste Regel gewinnt.</p>
                            {preview && (
                                <p className={`col-span-2 text-[11px] font-bold rounded-sm px-2 py-1 ${preview.count > 0 ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-gray-50 text-gray-500 border border-gray-200"}`} data-testid="b2b-trade-rule-preview">
                                    → betrifft aktuell <b>{preview.count}</b> {preview.count === 1 ? "Fahrzeug" : "Fahrzeuge"} deiner Organisation
                                    {preview.examples?.length > 0 && <span className="font-normal text-gray-500"> (z. B. {preview.examples.join(", ")})</span>}
                                </p>
                            )}
                        </div>
                    </div>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} data-testid="b2b-trade-rules-close">Schließen</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
